import React from 'react';
import Helmet from 'react-helmet';
import { useIntl } from '@kyruus/intl';
import { globalSiteNameMessages as globalMessages } from './website-json-ld';

/**
 * Wrapper for `react-helmet` that allows for internationalized titles and meta tags.
 * Sets the global site name in the `og:site_name` meta tag if defined in global messages.
 *
 * @param {Object} props - Props for the component.
 * @param {string|Object} props.title - The title of the page, either a string or a message descriptor.
 * @param {Object} [props.titleValues] - Values for the title message descriptor.
 * @param {Array<Object>} [props.meta] - Array of meta tag objects, each with a `message` and optional `values`.
 * @param {Array<Object>} [props.link] - Array of link tag objects.
 * @returns The rendered Helmet component with internationalized title and meta tags.
 */
export function IntlHelmet({ title, titleValues, meta = [], link, ...props }) {
  const intl = useIntl();

  const resolvedTitle =
    typeof title === 'string' ? title : intl.formatMessage(title, titleValues);

  const globalSiteName = intl.formatMessage(globalMessages.globalSiteName);

  const resolvedMeta = [
    ...(globalSiteName && globalSiteName !== globalMessages.globalSiteName.id
      ? [{ property: 'og:site_name', content: globalSiteName }]
      : []),
    ...meta.map(({ message, values, ...rest }) =>
      message ? { ...rest, content: intl.formatMessage(message, values) } : rest
    )
  ];

  return (
    <Helmet {...props} title={resolvedTitle} meta={resolvedMeta} link={link} />
  );
}
